import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {setTenantConfiguration} from "../../redux/slice/config/configSlice";
import {enqueueNotification} from "../../redux/slice/notification/notificationSlice";
import {RootState} from "../../redux/store";
import routes from "../../routes/routes";
import {TenantConfigService} from "../../types/tenantConfig";
import {getTenantName} from "../../util";
import {cn} from "../../util/cn";
import TenantConfig from "../../util/tenantConfig";
import Header from "../layout/components/Header";
import TenantLogo from "../layout/components/TenantLogo";
import Banners from "./components/Banners";
import ServiceCard from "./components/ServiceCard";
import useServiceStorage from "../../hooks/useServiceStorage";
import {ServiceTypeSelectorOption} from "../../types/order";

const HomePage = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const {config} = useSelector((state: RootState) => state.tenantConfig);
    const [serviceImages, setServiceImages] = useState<
        Record<TenantConfigService, string> | undefined
    >();

    const tenantName = getTenantName();
    if (!tenantName) throw new Error("Tenant name is not defined");

    const {updateService} = useServiceStorage(
        "selectedService",
        ServiceTypeSelectorOption.APARTMENT
    );

    function handleUpdateServiceType(service: ServiceTypeSelectorOption) {
        updateService(service);
    }

    useEffect(() => {
        const retrieveTenantConfig = async () => {
            const config = await TenantConfig();
            dispatch(setTenantConfiguration(config));
        };
        retrieveTenantConfig();
    }, [dispatch]);

    useEffect(() => {
        const loadImages = async () => {
            try {
                const [
                    p2p,
                    rental,
                    parcel,
                    reservation,
                    apartment,
                    storage,
                    sup,
                    voucher,
                    moreToCome,
                ] = await Promise.all([
                    import(`../../tenants/${tenantName}/assets/img/ClickandCollect.webp`), // p2p
                    import(
                        `../../tenants/${tenantName}/assets/img/privateHandOvers.webp`
                        ), //rental
                    import(`../../tenants/${tenantName}/assets/img/Product-rental.webp`), // parcel
                    import(`../../tenants/${tenantName}/assets/img/reservation.webp`), // reservation
                    import(`../../tenants/${tenantName}/assets/img/roomRental.webp`), // apartment
                    import(
                        `../../tenants/${tenantName}/assets/img/Temporary-storage.webp`
                        ), // storage
                    import(`../../tenants/${tenantName}/assets/img/sup.webp`), // sup
                    import(`../../tenants/${tenantName}/assets/img/voucher.webp`), // voucher
                    import(`../../tenants/${tenantName}/assets/img/moreToCome.webp`), // moreToCome
                ]);

                setServiceImages({
                    p2p: p2p.default,
                    rental: rental.default,
                    parcel: parcel.default,
                    reservation: reservation.default,
                    apartment: apartment.default,
                    storage: storage.default,
                    sup: sup.default,
                    voucher: voucher.default,
                    moreToCome: moreToCome.default,
                } as Record<TenantConfigService, string>);
            } catch (error) {
                dispatch(
                    enqueueNotification({
                        severity: "error",
                        message: `Failed to load images, error: ${error}`, // Todo: translate
                    })
                );
            }
        };
        loadImages();
    }, [tenantName, dispatch, t, config?.services]);

    return (
        <div className="min-h-full px-5 mb-20">
            <Header>
                <TenantLogo/>
            </Header>

            <Banners/>

            <span className="flex justify-center mb-4 text-base leading-5 tracking-[.02rem]">
        {t("home.do")}
      </span>
            {/* Services Section */}
            <div
                className={cn(
                    "items-center justify-center gap-4",
                    config?.services.length > 2 ? "grid grid-cols-2" : "flex flex-col"
                )}
            >
                {config?.services.map((serviceType) => {
                    switch (serviceType) {
                        case "storage":
                            return (
                                <ServiceCard
                                    key="storage"
                                    service={ServiceTypeSelectorOption.STORAGE}
                                    onClickService={handleUpdateServiceType}
                                    label={t("home.temporary_storage")}
                                    to={routes.temStorageNewBooking}
                                    imagePath={serviceImages?.storage}
                                />
                            );
                        case "rental":
                            return (
                                <ServiceCard
                                    key="rental"
                                    service={ServiceTypeSelectorOption.RENTAL}
                                    onClickService={handleUpdateServiceType}
                                    label={t("home.product_rental")}
                                    to={routes.bookLocationEquipmentRentalPath}
                                    imagePath={serviceImages?.rental}
                                />
                            );
                        case "p2p":
                            return (
                                <ServiceCard
                                    key="p2p"
                                    service={ServiceTypeSelectorOption.P2P}
                                    onClickService={handleUpdateServiceType}
                                    label={t("home.private_handovers")}
                                    to={routes.bookLocationP2PPath}
                                    imagePath={serviceImages?.p2p}
                                />
                            );
                        case "parcel":
                            return (
                                <ServiceCard
                                    key="parcel"
                                    service={ServiceTypeSelectorOption.PARCEL}
                                    onClickService={handleUpdateServiceType}
                                    label={t("home.click_and_collect")}
                                    to={routes.bookParcelDeliveryPath}
                                    imagePath={serviceImages?.parcel}
                                />
                            );
                        case "apartment":
                            return (
                                <ServiceCard
                                    key="apartment"
                                    service={ServiceTypeSelectorOption.APARTMENT}
                                    onClickService={handleUpdateServiceType}
                                    label={t("home.room_rental")}
                                    to={routes.roomRentalPath}
                                    imagePath={serviceImages?.apartment}
                                />
                            );
                        case "reservation":
                            return (
                                <ServiceCard
                                    key="reservation"
                                    service={ServiceTypeSelectorOption.APARTMENT}
                                    onClickService={handleUpdateServiceType}
                                    label={t("home.venue_reservation")}
                                    to={routes.venueReservationPath}
                                    imagePath={serviceImages?.reservation}
                                />
                            );
                        case "sup":
                            return (
                                <ServiceCard
                                    key="sup"
                                    service={ServiceTypeSelectorOption.SUP}
                                    onClickService={handleUpdateServiceType}
                                    label={t("home.product_sup")}
                                    to={routes.bookLocationSUPPath}
                                    imagePath={serviceImages?.sup}
                                />
                            );
                        case "voucher":
                            return (
                                <ServiceCard
                                    key="voucher"
                                    service={ServiceTypeSelectorOption.VOUCHER}
                                    onClickService={handleUpdateServiceType}
                                    label={t("home.voucher_card")}
                                    to={routes.voucherPath}
                                    imagePath={serviceImages?.voucher}
                                />
                            );
                        case "moreToCome":
                            return (
                                <ServiceCard
                                    key="moreToCome"
                                    service={ServiceTypeSelectorOption.ALL}
                                    onClickService={handleUpdateServiceType}
                                    label={t("home.more_to_come")}
                                    to={routes.moreToComePath}
                                    imagePath={serviceImages?.moreToCome}
                                />
                            );
                        default:
                            return null;
                    }
                })}
            </div>
        </div>
    );
};
export default HomePage;
